import { BiSolidChevronDown, BiSolidChevronUp } from 'react-icons/bi';

import { round1 } from '@/utils/unitConversion';

import s from './NumberArrows.module.scss';

interface NumberArrowsProps {
  value: string;
  onChange: (value: string) => void;
  /** As on the input: `min` floors the down arrow, a numeric `step` sets the increment (default 1). */
  min?: number | string;
  step?: number | string;
  disabled?: boolean;
  /** Prefix for the buttons' accessible names ("Fuel onboard 1: increase"). */
  name?: string;
}

const toNumber = (v: number | string | undefined): number | undefined => {
  const n = typeof v === 'number' ? v : parseFloat(v ?? '');
  return Number.isNaN(n) ? undefined : n;
};

/**
 * Up/down arrows beside a compact number input. Stands in for the browser's
 * spin buttons, which iOS and Android never draw and which are too small to
 * hit on desktop. Not tab stops — the input already steps on ↑/↓.
 */
export function NumberArrows({
  value,
  onChange,
  min,
  step,
  disabled,
  name = '',
}: NumberArrowsProps) {
  const current = toNumber(value) ?? 0;
  const floor = toNumber(min);
  const delta = toNumber(step) ?? 1;
  const atFloor = floor !== undefined && current <= floor;

  const set = (next: number) =>
    onChange(String(floor === undefined ? round1(next) : Math.max(floor, round1(next))));

  return (
    <div className={s.root}>
      <button
        type="button"
        tabIndex={-1}
        className={s.arrow}
        aria-label={`${name}: increase`.trim()}
        disabled={disabled}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => set(current + delta)}
      >
        <BiSolidChevronUp aria-hidden="true" />
      </button>
      <button
        type="button"
        tabIndex={-1}
        className={s.arrow}
        aria-label={`${name}: decrease`.trim()}
        disabled={disabled || atFloor}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => set(current - delta)}
      >
        <BiSolidChevronDown aria-hidden="true" />
      </button>
    </div>
  );
}
